/**
 * Pure helpers for reasoning about Cookiebot's `CookieConsent` cookie value.
 *
 * The value is vendor-defined and deliberately treated as opaque here. We only ever read
 * the `utc` field out of it so two recorded decisions can be ordered, and check that it is
 * a real explicit decision rather than one of Cookiebot's sentinels or guesses. We never
 * build one, and never alter the categories inside it.
 *
 * An explicit decision looks like this (URL-encoded in the cookie):
 * `{stamp:'<base64>',necessary:true,preferences:false,statistics:false,marketing:false,method:'explicit',ver:1,utc:1789137696488,region:'gb'}`
 *
 * Values that are NOT decisions, and may never be copied between hosts:
 *  - bare sentinels: `-1` (out of region, treated by Cookiebot as full consent), `-2`
 *    (implied-consent placeholder), `-3` (a transient cookie-support probe) and `0`;
 *  - the out-of-region value in its JSON shape, which `setOutOfRegion` (also used for
 *    detected spiders) writes with a ONE-month expiry:
 *    `{stamp:'-1',necessary:true,preferences:true,statistics:true,marketing:true,method:'implied',ver:1,utc:N,region:'us'}`.
 *    It carries a `utc` like a real decision, so the stamp and method must be inspected,
 *    not just the shape;
 *  - anything else with `method:'implied'`, which is Cookiebot's guess, not a choice.
 */

/**
 * Matches the current 12-month ClearScore configuration and the expiry its server emits. Keep this limit aligned with Cookiebot Manager if that configuration changes.
 * The captured `logconsent.ashx` response sets `new Date(utc + 365 days)`.
 */
export const CONSENT_LIFETIME_MS = 365 * 24 * 60 * 60 * 1000;

/** Explicit sharing scopes; never guess a parent from an arbitrary hostname. */
const SHARE_DOMAINS = [
    'clearscore.com',
    'drivescore.com',
    'cs55.co.uk',
    'cslocal.71a.dev',
    'clearscore.staging.71a.dev',
    'clearscore.dev.71a.dev',
    'clearscore.frontend.localhost',
];

export const getParentCookieDomain = (hostname: string): string | null => {
    const domain = SHARE_DOMAINS.find((candidate) => hostname === candidate || hostname.endsWith(`.${candidate}`));
    return domain ? `.${domain}` : null;
};

/**
 * Name of the parent-domain mirror for one Cookiebot domain group.
 *
 * Keyed by the group id so that hosts which share a parent domain but belong to different
 * groups, which happens on shared staging domains, never read each other's decisions.
 */
export const getMirrorCookieName = (cookiebotId: string): string => `CS_COOKIE_CONSENT_${cookiebotId}`;

/** The value with its URL encoding removed, or the raw value when the encoding is malformed. */
const decodeValue = (value: string): string => {
    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
};

/** Milliseconds since epoch recorded in a consent value, or null when it cannot be read. */
export const parseConsentUtc = (value: string): number | null => {
    const match = /(?:^|[,\{])utc:(\d+)(?=[,}])/.exec(decodeValue(value));
    if (!match) return null;

    const utc = Number(match[1]);
    return Number.isSafeInteger(utc) && utc > 0 ? utc : null;
};

/**
 * Whether a value is a decision the visitor explicitly made, as opposed to one of
 * Cookiebot's sentinels, its implied out-of-region value, or something malformed. Only
 * these may be carried between hosts.
 *
 * The stamp must not be a bare number: Cookiebot's sentinels (`-1`, `-2`, `-3`, `0`) show
 * up inside the JSON shape too, most importantly the out-of-region write with
 * `stamp:'-1'`.
 */
export const isRecordedDecision = (value: string | null | undefined): value is string => {
    if (!value) return false;

    const decoded = decodeValue(value);

    return (
        decoded.charAt(0) === '{' &&
        /stamp:'(?!-?\d+')[^']+'/.test(decoded) &&
        /method:'explicit'/.test(decoded) &&
        parseConsentUtc(value) !== null
    );
};

/** When Cookiebot would let a recorded decision lapse, or null when the value is not one. */
export const consentExpiresAt = (value: string): Date | null => {
    const utc = parseConsentUtc(value);
    if (utc === null) return null;
    const expires = new Date(utc + CONSENT_LIFETIME_MS);
    return Number.isFinite(expires.getTime()) ? expires : null;
};

/** Only replay decisions within the configured lifetime, never future-dated values. */
export const isCurrentDecision = (value: string | null | undefined): value is string => {
    if (!isRecordedDecision(value)) return false;
    const utc = parseConsentUtc(value);
    const expires = consentExpiresAt(value);
    const now = Date.now();
    return utc !== null && utc <= now && expires !== null && expires.getTime() > now;
};

/**
 * Whether `candidate` records a strictly more recent decision than `existing`.
 *
 * Returns false whenever either side cannot be dated. That is the safe answer: an
 * undateable value never overwrites a decision already recorded on the host.
 */
export const isNewerConsent = (candidate: string, existing: string): boolean => {
    const candidateUtc = parseConsentUtc(candidate);
    const existingUtc = parseConsentUtc(existing);
    if (candidateUtc === null || existingUtc === null) return false;
    return candidateUtc > existingUtc;
};

/**
 * Which of a host's own value and the mirrored value should be honoured.
 *
 * A recorded decision always beats a sentinel or nothing, because the sentinels are
 * Cookiebot's regional guesses about this host while a recorded decision is something the
 * visitor explicitly chose. Between two recorded decisions the newer wins. Used both by the
 * client seed and by the server-side read, so the two agree.
 */
export const pickNewestConsent = (
    hostConsent: string | null | undefined,
    mirroredConsent: string | null | undefined,
): string | undefined => {
    const hostRecorded = isCurrentDecision(hostConsent);
    const mirrorRecorded = isCurrentDecision(mirroredConsent);

    if (!mirrorRecorded) return isRecordedDecision(hostConsent) && !hostRecorded ? undefined : hostConsent ?? undefined;
    if (!hostRecorded) return mirroredConsent;
    return isNewerConsent(mirroredConsent, hostConsent) ? mirroredConsent : hostConsent;
};
